import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '@/config/env';

interface ApiClientConfig {
  getAuthToken?: () => string | null;
  getQueryDefaults?: () => Record<string, string>;
  onUnauthorized?: () => void;
}

let config: ApiClientConfig = {};

export function configureApiClient(next: ApiClientConfig) {
  config = next;
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: 'application/json' },
});

http.interceptors.request.use((request) => {
  const token = config.getAuthToken?.();
  if (token) request.headers.set('Authorization', `Bearer ${token}`);

  const defaults = config.getQueryDefaults?.();
  if (defaults) request.params = { ...defaults, ...(request.params ?? {}) };

  return request;
});

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (!axios.isAxiosError(error)) throw error;

    const { response, message } = error as AxiosError<unknown>;
    if (!response) throw new ApiError(0, message, null);

    if (response.status === 401) config.onUnauthorized?.();

    const body = response.data;
    const detail =
      typeof body === 'object' && body !== null
        ? ((body as { message?: unknown; detail?: unknown }).message ??
          (body as { detail?: unknown }).detail)
        : undefined;

    throw new ApiError(response.status, detail ? String(detail) : response.statusText, body);
  },
);

type Options = Omit<AxiosRequestConfig, 'url' | 'method' | 'data' | 'baseURL'>;

async function send<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  path: string,
  body?: unknown,
  options?: Options,
): Promise<T> {
  const response = await http.request<T>({ ...options, url: path, method, data: body });
  return response.data;
}

export const apiClient = {
  get: <T>(path: string, options?: Options) => send<T>('get', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: Options) =>
    send<T>('post', path, body, options),
  put: <T>(path: string, body?: unknown, options?: Options) => send<T>('put', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: Options) =>
    send<T>('patch', path, body, options),
  delete: <T>(path: string, options?: Options) => send<T>('delete', path, undefined, options),

  postBlob: (path: string, body?: unknown, options?: Options) =>
    send<Blob>('post', path, body, { ...options, responseType: 'blob' }),

  // Axios sets the multipart boundary itself for FormData, which is why no
  // Content-Type is passed here. `PUT` is a method because one endpoint replaces
  // a whole document — settings and files together — rather than adding a file.
  upload: <T>(
    path: string,
    form: FormData,
    method: 'post' | 'put' = 'post',
    options?: Options,
  ) => send<T>(method, path, form, options),
};
